import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Checkbox } from 'antd';
import styled from 'styled-components';

import { getShopRequests } from 'apis/ShopRequestAPI';
import { isMysteryShopAdmin, isMysteryShopShopper } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';
import { useDebounce } from 'utils/hooks/useDebounce';

import ShopRequestSortIcon from './ShopRequestSortIcon';
import ShopRequestsStyledTable from './ShopRequestsStyledTable';
import { SHOP_REQUEST_COLUMNS, SHOP_REQUEST_VIEW_GROUP_BY, SHOP_REQUESTS_PAGE_SIZE } from '../constants';
import EditBatchShopRequestsModal from '../EditBatchShopRequestsModal/EditBatchShopRequestsModal';
import EditShopRequestModal from '../EditShopRequestModal/EditShopRequestModal';
import { useShopRequestsDataContext } from '../ShopRequestsDataContext';
import ShopRequestCommunity from '../TableColumns/ShopRequestCommunity';
import ShopRequestsDueDate from '../TableColumns/ShopRequestsDueDate';
import ShopRequestsPropagation from '../TableColumns/ShopRequestsPropagation';
import ShopRequestsStatus from '../TableColumns/ShopRequestsStatus';
import ShopRequestsUpdatedAt from '../TableColumns/ShopRequestsUpdatedAt';
import ShopRequestsUserAssignment from '../TableColumns/ShopRequestsUserAssignment';
import { MysteryShopRequestInbound } from '../types';
import {
  getShopRequestListingPayload,
  getShopRequestOrderingFromSorter,
  getShopRequestPageFromTableChange,
  getShopRequestSortOrder,
} from '../utils';

const Container = styled.div`
  flex-grow: 1;
  background: var(--background-gray);
  padding: 0 10px;
  border-top: solid 1px var(--line-gray);
`;

const ShopRequestsTable = () => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const {
    ordering,
    filters,
    selectionMode,
    selectedItemsIds,
    openEditBatchShopRequestsModel,
    setPage,
    setOrdering,
    setSelectedItemsIds,
    setOpenEditBatchShopRequestsModel,
    setItems,
    setTotalItemsIds,
    handleSelection,
  } = useShopRequestsDataContext();
  const [modifyingShopRequest, setModifyingShopRequest] = useState<MysteryShopRequestInbound | null>(null);

  const debouncedSearch = useDebounce(filters.search, 300);
  const debouncedFilters = { ...filters, search: debouncedSearch };
  const isGroupedByRequest = filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.REQUEST;
  const isGroupedByCommunity = filters.groupBy === SHOP_REQUEST_VIEW_GROUP_BY.COMMUNITY;
  const enableShopRequests = !isGroupedByRequest && !isGroupedByCommunity && debouncedSearch !== undefined;

  const {
    isLoading,
    isFetching,
    data,
    refetch: refetchData,
  } = useQuery({
    queryKey: ['shopRequests', debouncedFilters, ordering],
    queryFn: ({ signal }) => getShopRequests(getShopRequestListingPayload(debouncedFilters, ordering), signal),
    onSuccess: (data) => {
      setItems(data);
      setTotalItemsIds(data.ids);

      // Handle a new selection once filters are updated
      // New selection is filtered out previous selection
      const selectionIntersection = selectedItemsIds.filter((id) => data.ids.includes(id));
      setSelectedItemsIds(selectionIntersection);
    },
    enabled: enableShopRequests,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const onSelect = (record: any, selected: any, selectedRows: any, nativeEvent: any) => {
    const itemKey = record.id;
    let newSelection: any[] = [];

    if (selected) {
      newSelection = [...selectedItemsIds, itemKey];
    } else {
      const index = selectedItemsIds.indexOf(itemKey);
      if (index > -1) {
        newSelection = [...selectedItemsIds];
        newSelection.splice(index, 1);
      }
    }

    setSelectedItemsIds(newSelection);
    handleSelection(newSelection);
  };

  const onSelectAll = (selected: any, selectedRows: any, changeRows: any) => {
    let newSelection: any[] = [];

    if (selected) {
      newSelection = selectedRows.map((row: any) => row.id);
      setSelectedItemsIds(newSelection);
    } else {
      setSelectedItemsIds(newSelection);
    }

    setSelectedItemsIds(newSelection);
    handleSelection(newSelection);
  };

  const isShopper =
    currentUser && isMysteryShopShopper(currentUser) && !currentUser.isSuperuser && !isMysteryShopAdmin(currentUser);

  const pageItemIds = data?.results?.map((el: any) => el.id) ?? [];
  const pageSelectionSet = new Set(pageItemIds);
  const selectedOnPage = selectedItemsIds.filter((id) => pageSelectionSet.has(id));
  const isAllPageSelected = selectionMode && pageItemIds.length > 0 && selectedOnPage.length === pageItemIds.length;
  const isPageIndeterminate = selectionMode && selectedOnPage.length > 0 && !isAllPageSelected;

  const handleHeaderCheckboxChange = (checked: boolean) => {
    if (checked) {
      const newSelection = Array.from(new Set([...selectedItemsIds, ...pageItemIds]));
      setSelectedItemsIds(newSelection);
      handleSelection(newSelection);
    } else {
      const newSelection = selectedItemsIds.filter((id) => !pageSelectionSet.has(id));
      setSelectedItemsIds(newSelection);
      handleSelection(newSelection);
    }
  };

  const columns = [
    {
      id: SHOP_REQUEST_COLUMNS.community.id,
      key: SHOP_REQUEST_COLUMNS.community.id,
      title: SHOP_REQUEST_COLUMNS.community.label,
      width: '27%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.community.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => <ShopRequestCommunity shopRequest={record} />,
    },
    {
      id: SHOP_REQUEST_COLUMNS.propagation.id,
      key: SHOP_REQUEST_COLUMNS.propagation.id,
      title: SHOP_REQUEST_COLUMNS.propagation.label,
      width: '7%',
      render: (value: any, record: any) => <ShopRequestsPropagation shopRequest={record} />,
      hidden: Boolean(isShopper),
    },
    {
      id: SHOP_REQUEST_COLUMNS.shopper.id,
      key: SHOP_REQUEST_COLUMNS.shopper.id,
      title: SHOP_REQUEST_COLUMNS.shopper.label,
      width: '18%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.shopper.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => (
        <ShopRequestsUserAssignment
          shopRequest={record}
          fieldKey="shopper"
          fieldKeyId="shopper_id"
          label={SHOP_REQUEST_COLUMNS.shopper.label}
        />
      ),
    },
    {
      id: SHOP_REQUEST_COLUMNS.auditor.id,
      key: SHOP_REQUEST_COLUMNS.auditor.id,
      title: SHOP_REQUEST_COLUMNS.auditor.label,
      width: '16%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.auditor.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => (
        <ShopRequestsUserAssignment
          shopRequest={record}
          fieldKey="auditor"
          fieldKeyId="auditor_id"
          label={SHOP_REQUEST_COLUMNS.auditor.label}
        />
      ),
    },
    {
      id: SHOP_REQUEST_COLUMNS.status.id,
      key: SHOP_REQUEST_COLUMNS.status.id,
      title: SHOP_REQUEST_COLUMNS.status.label,
      width: '11%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.status.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => <ShopRequestsStatus shopRequest={record} />,
    },
    {
      id: SHOP_REQUEST_COLUMNS.updated_at.id,
      key: SHOP_REQUEST_COLUMNS.updated_at.id,
      title: SHOP_REQUEST_COLUMNS.updated_at.label,
      width: '10%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.updated_at.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => <ShopRequestsUpdatedAt shopRequest={record} />,
    },
    {
      id: SHOP_REQUEST_COLUMNS.dueDate.id,
      key: SHOP_REQUEST_COLUMNS.dueDate.id,
      title: SHOP_REQUEST_COLUMNS.dueDate.label,
      width: '11%',
      sorter: true,
      sortOrder: getShopRequestSortOrder(ordering, SHOP_REQUEST_COLUMNS.dueDate.id),
      sortIcon: ({ sortOrder }: any) => <ShopRequestSortIcon sortOrder={sortOrder} />,
      render: (value: any, record: any) => <ShopRequestsDueDate shopRequest={record} />,
    },
  ];

  return (
    <Container>
      <ShopRequestsStyledTable
        columns={columns}
        dataSource={data?.results?.map((el: any) => ({ ...el, key: el.id }))}
        loading={isLoading || isFetching}
        onRow={(record) => ({
          onClick: () => setModifyingShopRequest(record as MysteryShopRequestInbound),
        })}
        rowKey={(record: any) => record?.id}
        rowSelection={
          selectionMode
            ? {
                selectedRowKeys: selectedItemsIds,
                preserveSelectedRowKeys: true,
                onSelect: onSelect,
                onSelectAll,
                columnTitle: (
                  <Checkbox
                    data-test-id="btn-bulk-select-all"
                    indeterminate={isPageIndeterminate}
                    checked={isAllPageSelected}
                    onChange={(event) => handleHeaderCheckboxChange(event.target.checked)}
                  />
                ),
              }
            : undefined
        }
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: filters.page,
          pageSize: SHOP_REQUESTS_PAGE_SIZE,
          total: data?.count,
          onChange: (pageNumber: number) => {
            setPage(pageNumber);
          },
          showTotal: (total, range) => {
            const displayedItems = range[1] - range[0] + 1;
            return (
              <span>
                Showing {displayedItems} of {total} records
              </span>
            );
          },
        }}
        onChange={(pagination: any, filters: any, sorter: any, extra: any) => {
          if (sorter) {
            const orderBy = getShopRequestOrderingFromSorter(sorter);
            if (orderBy !== ordering) {
              setOrdering(orderBy);
            }
          }
          const page = getShopRequestPageFromTableChange(pagination, extra);
          if (page) {
            setPage(page);
          }
        }}
      />
      {modifyingShopRequest && (
        <EditShopRequestModal
          shopRequest={modifyingShopRequest}
          refetchData={refetchData}
          onCancel={() => setModifyingShopRequest(null)}
        />
      )}
      {openEditBatchShopRequestsModel && (
        <EditBatchShopRequestsModal
          refetchData={refetchData}
          onCancel={() => setOpenEditBatchShopRequestsModel(false)}
        />
      )}
    </Container>
  );
};

export default ShopRequestsTable;
